import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PermissionAccessProvider, PermissionAwareButton, PermissionAwareInput, PermissionAwareSelect } from '../../../contexts/PermissionAccessContext';
import { inputClass, str } from './unitFields';
import { YES_NO_OPTIONS } from './unitForm';
import { ChildRow, applyViolationLookup, recomputeTotal, setViolationOos, totalTicketWeight } from './ticketForm';
import { ViolationTypeahead } from './ticketTypeaheads';

/**
 * Violations / Equipment tabs of the Ticket drawer (the History tab is the shared
 * `SafetyHistoryTab` with `TICKET_HISTORY_LABELS`). The two editable tabs are row grids:
 * each cell is a PermissionAware control labelled "<Column> <n>" so the rows stay
 * reachable by label. Removing an existing row marks it `deleted:'Y'` (hidden here,
 * posted as a delete marker); removing a new row just drops it.
 */

/** `ticket_history.dbtable` -> display label. */
export const TICKET_HISTORY_LABELS: Record<string, string> = { ticket: 'Ticket', ticket_violation: 'Violation', ticket_equipment: 'Equipment', notes: 'Note' };

const live = (rows: ChildRow[]) => rows.filter((r) => str(r.deleted).toUpperCase() !== 'Y');
const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';
const td = 'px-1 py-0.5 align-top';
const cell = `${inputClass} min-w-[4rem]`;

function YnSelect({ label, value, onChange }: { label: string; value: unknown; onChange: (v: string) => void }) {
  return (
    <PermissionAwareSelect aria-label={label} className={cell} value={str(value)} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {YES_NO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </PermissionAwareSelect>
  );
}

// ---- Violations -------------------------------------------------------------

export interface ViolationsTabProps {
  rows: ChildRow[];
  onRowsChange: (rows: ChildRow[]) => void;
  equipmentUnits: string[];
  canEdit: boolean;
  onAdd: () => void;
}

export function ViolationsTab({ rows, onRowsChange, equipmentUnits, canEdit, onAdd }: ViolationsTabProps) {
  const visible = live(rows);
  const update = (key: string, patch: (row: ChildRow) => ChildRow) =>
    onRowsChange(rows.map((r) => (r._key === key ? patch(r) : r)));
  const remove = (key: string) =>
    onRowsChange(rows.flatMap((r) => (r._key !== key ? [r] : r.recno ? [{ ...r, deleted: 'Y' }] : [])));
  const unitOptions = Array.from(new Set(['D', ...equipmentUnits.map((u) => str(u).trim()).filter(Boolean)]));

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="space-y-2">
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className={th}>#</th>
                <th className={th}>Ticketed</th>
                <th className={th}>Violation</th>
                <th className={th}>Description</th>
                <th className={th}>Unit</th>
                <th className={th}>OOS</th>
                <th className={th}>Severity</th>
                <th className={th}>Time</th>
                <th className={th}>Total</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={10} className="px-2 py-4 text-center text-sm text-muted">No violations on this ticket.</td></tr>
              )}
              {visible.map((row, i) => {
                const n = i + 1;
                const key = row._key as string;
                const unitValue = str(row.violationunit).trim();
                return (
                  <tr key={key}>
                    <td className={`${td} pt-2 text-xs text-muted`}>{n}</td>
                    <td className={td}><YnSelect label={`Ticketed ${n}`} value={row.wasticketed} onChange={(v) => update(key, (r) => ({ ...r, wasticketed: v }))} /></td>
                    <td className={`${td} min-w-[9rem]`}>
                      <ViolationTypeahead ariaLabel={`Violation ${n}`} value={str(row.violation)}
                        onChange={(text) => update(key, (r) => ({ ...r, violation: text }))}
                        onPick={(hit) => update(key, (r) => applyViolationLookup(r, hit))} />
                    </td>
                    <td className={`${td} min-w-[14rem]`}>
                      <PermissionAwareInput aria-label={`Description ${n}`} className={cell} value={str(row.violationdesc)}
                        onChange={(e) => update(key, (r) => ({ ...r, violationdesc: e.target.value }))} />
                    </td>
                    <td className={td}>
                      <PermissionAwareSelect aria-label={`Unit ${n}`} className={cell} value={unitValue}
                        onChange={(e) => update(key, (r) => ({ ...r, violationunit: e.target.value }))}>
                        {!unitOptions.includes(unitValue) && <option value={unitValue}>{unitValue || '—'}</option>}
                        {unitOptions.map((u) => <option key={u} value={u}>{u === 'D' ? 'D (driver)' : u}</option>)}
                      </PermissionAwareSelect>
                    </td>
                    <td className={td}><YnSelect label={`OOS ${n}`} value={row.violationoos} onChange={(v) => update(key, (r) => setViolationOos(r, v))} /></td>
                    <td className={td}>
                      <PermissionAwareInput type="number" aria-label={`Severity ${n}`} className={`${cell} w-20`} value={Number(row.severityweight) || 0}
                        onChange={(e) => update(key, (r) => recomputeTotal({ ...r, severityweight: e.target.value }))} />
                    </td>
                    <td className={td}>
                      <PermissionAwareInput type="number" aria-label={`Time ${n}`} className={`${cell} w-16`} value={Number(row.timeweight) || 0}
                        onChange={(e) => update(key, (r) => recomputeTotal({ ...r, timeweight: e.target.value }))} />
                    </td>
                    <td className={td}>
                      <input type="number" aria-label={`Total ${n}`} className={`${cell} w-16`} value={Number(row.totalweight) || 0} readOnly disabled />
                    </td>
                    <td className={td}>
                      <PermissionAwareButton type="button" aria-label={`Remove violation ${n}`} onClick={() => remove(key)}
                        className="rounded p-1 text-muted hover:bg-highlight hover:text-danger-fg disabled:cursor-not-allowed">
                        <TrashIcon width={16} aria-hidden="true" />
                      </PermissionAwareButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between">
          <PermissionAwareButton type="button" onClick={onAdd}
            className="inline-flex items-center gap-1 rounded-md border border-rule px-2 py-1 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled">
            <PlusIcon width={14} aria-hidden="true" />Add violation
          </PermissionAwareButton>
          <span className="text-sm">Total weight: <strong>{totalTicketWeight(rows)}</strong></span>
        </div>
      </div>
    </PermissionAccessProvider>
  );
}

// ---- Equipment --------------------------------------------------------------

export interface EquipmentTabProps {
  rows: ChildRow[];
  onRowsChange: (rows: ChildRow[]) => void;
  owners: string[];
  canEdit: boolean;
  onAdd: () => void;
}

const EQUIP_TEXT: { key: string; label: string; width: string; maxLength?: number }[] = [
  { key: 'ticketequipunit', label: 'Equip Unit', width: 'min-w-[6rem]' },
  { key: 'ticketequiptype', label: 'Equip Type', width: 'min-w-[7rem]' },
  { key: 'ticketequipmake', label: 'Make', width: 'min-w-[7rem]' },
  { key: 'ticketequipco', label: 'Company', width: 'min-w-[7rem]' },
  { key: 'ticketequipplate', label: 'Plate', width: 'min-w-[6rem]' },
  { key: 'ticketequipstate', label: 'State', width: 'w-16', maxLength: 2 },
  { key: 'ticketequipvin', label: 'VIN', width: 'min-w-[11rem]' },
];

export function EquipmentTab({ rows, onRowsChange, owners, canEdit, onAdd }: EquipmentTabProps) {
  const visible = live(rows);
  const update = (key: string, patch: Record<string, unknown>) =>
    onRowsChange(rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  const remove = (key: string) =>
    onRowsChange(rows.flatMap((r) => (r._key !== key ? [r] : r.recno ? [{ ...r, deleted: 'Y' }] : [])));
  const ownerOptions = Array.from(new Set(['SELF', ...owners]));

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="space-y-2">
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className={th}>#</th>
                {EQUIP_TEXT.map((c) => <th key={c.key} className={th}>{c.label}</th>)}
                <th className={th}>Owner</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={EQUIP_TEXT.length + 3} className="px-2 py-4 text-center text-sm text-muted">No equipment on this ticket.</td></tr>
              )}
              {visible.map((row, i) => {
                const n = i + 1;
                const key = row._key as string;
                const owner = str(row.ticketequipowner);
                return (
                  <tr key={key}>
                    <td className={`${td} pt-2 text-xs text-muted`}>{n}</td>
                    {EQUIP_TEXT.map((c) => (
                      <td key={c.key} className={`${td} ${c.width}`}>
                        <PermissionAwareInput aria-label={`${c.label} ${n}`} className={cell} value={str(row[c.key])} maxLength={c.maxLength}
                          onChange={(e) => update(key, { [c.key]: e.target.value })} />
                      </td>
                    ))}
                    <td className={td}>
                      <PermissionAwareSelect aria-label={`Equip Owner ${n}`} className={cell} value={owner}
                        onChange={(e) => update(key, { ticketequipowner: e.target.value })}>
                        {owner && !ownerOptions.includes(owner) && <option value={owner}>{owner}</option>}
                        {ownerOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                      </PermissionAwareSelect>
                    </td>
                    <td className={td}>
                      <PermissionAwareButton type="button" aria-label={`Remove equipment ${n}`} onClick={() => remove(key)}
                        className="rounded p-1 text-muted hover:bg-highlight hover:text-danger-fg disabled:cursor-not-allowed">
                        <TrashIcon width={16} aria-hidden="true" />
                      </PermissionAwareButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <PermissionAwareButton type="button" onClick={onAdd}
          className="inline-flex items-center gap-1 rounded-md border border-rule px-2 py-1 text-sm hover:bg-highlight disabled:cursor-not-allowed disabled:text-disabled">
          <PlusIcon width={14} aria-hidden="true" />Add equipment
        </PermissionAwareButton>
      </div>
    </PermissionAccessProvider>
  );
}

// ---- History ----------------------------------------------------------------
